/**
 * The tenant home, `/t/$tenantId` ("Campanhas" in the nav and the trail): one toolbar row and the
 * campaign cards, nothing else. Credits, slots and monitored accounts live on Créditos
 * (`/t/$tenantId/creditos`), where a newly created organisation lands.
 *
 * The toolbar carries a campaigns/members counts line (each gated on its own
 * query settling, so neither figure prints a zero it has not measured) on the left, and
 * "Nova campanha" on the right. That action is disabled, with its reason attached, until capacity shows a free
 * slot; the API's 409 is the real refusal (§5.1.1). Creating opens `NewCampaignDialog` over this
 * page, from the header or the list's own empty state.
 */

import { useId, useState, type ReactNode } from 'react';
import { useParams } from '@tanstack/react-router';
import { Plus } from 'lucide-react';

import { Page, PageHeader } from '../../../chrome/page';
import { Button } from '../../../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../ui/tooltip';
import { NewCampaignDialog } from '../../campaign/record/NewCampaignDialog';
import { useCan } from '../../session/orgContext';
import { useCapacity } from '../capacity/api/queries';
import { tt } from '../i18n';
import { nt } from '../i18n/nav';
import { useMembers } from '../members/useMembers';
import { CampaignCards } from './CampaignCards';
import { hm, hmPlural } from './messages';
import { useCampaigns } from './useCampaigns';

function useTenantId(): string {
  const params = useParams({ strict: false }) as { tenantId?: string };
  if (params.tenantId === undefined) {
    throw new Error('TenantHome mounted with no tenantId in the route params');
  }
  return params.tenantId;
}

export function TenantHome(): ReactNode {
  const tenantId = useTenantId();
  const [creating, setCreating] = useState(false);
  // A courtesy: the API refuses a create from a caller without the grant
  // (platform design §17.2), and absent rather than disabled is §17.15's rule.
  const canCreate = useCan('control_plane.write');
  const { data: capacity } = useCapacity(tenantId);
  const { data: campaigns } = useCampaigns(tenantId);
  const { data: members } = useMembers(tenantId);
  // Absent, not zero, until both counts have actually been measured.
  const countsLine =
    campaigns === undefined || members === undefined
      ? undefined
      : hm('counts.line', {
          campaigns: hmPlural('counts.campaigns.other', campaigns.length, {
            count: campaigns.length,
          }),
          members: hmPlural('counts.members.other', members.length, { count: members.length }),
        });
  // Closed until capacity has loaded: offering a create the API may refuse is the wrong guess.
  const slotFree =
    capacity !== undefined && capacity.slots_available !== null && capacity.slots_available > 0;
  const noSlotReasonId = useId();

  return (
    <Page>
      <PageHeader
        title={nt('home')}
        description={countsLine}
        actions={
          canCreate ? (
            slotFree ? (
              <Button onClick={() => setCreating(true)}>
                <Plus aria-hidden className="size-4" />
                {tt('home.newCampaign')}
              </Button>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  {/* `aria-disabled`, not `disabled`: a disabled button takes no focus or hover,
                      so neither a keyboard nor a pointer user could reach the reason. */}
                  <TooltipTrigger asChild>
                    <Button
                      aria-disabled="true"
                      aria-describedby={noSlotReasonId}
                      className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                    >
                      <Plus aria-hidden className="size-4" />
                      {tt('home.newCampaign')}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{hm('newCampaign.noSlot')}</TooltipContent>
                </Tooltip>
                <span id={noSlotReasonId} className="sr-only">
                  {hm('newCampaign.noSlot')}
                </span>
              </TooltipProvider>
            )
          ) : undefined
        }
      />

      <div data-testid="home-block-campaigns">
        <CampaignCards
          tenantId={tenantId}
          onCreate={canCreate && slotFree ? () => setCreating(true) : undefined}
          createBlocked={canCreate && capacity !== undefined && !slotFree}
        />
      </div>

      <NewCampaignDialog tenantId={tenantId} open={creating} onOpenChange={setCreating} />
    </Page>
  );
}
